import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { StateSetter, TruthReviewQueue, TruthSuggestion } from '../types'

// TruthContext owns the review-first truth proposal state previously held
// in App.jsx as two coupled useState entries: `truthSuggestions` (the flat
// list returned by inference) and `truthReviewQueue` (the structured
// `{ groups, coverage, suggestions }` shape from /api/truth/review-queue).
//
// Wave 7 Phase B — extracts both slots plus the small reducer that keeps
// them in sync (`applyReviewQueue`). The reducer is pure state logic and
// belongs with the storage; consumers should not have to re-derive
// `suggestions` from a queue payload at every call site.
// Wave 15 — promoted to TypeScript.

export interface TruthContextValue {
  suggestions: TruthSuggestion[]
  reviewQueue: TruthReviewQueue
  setSuggestions: StateSetter<TruthSuggestion[]>
  setReviewQueue: StateSetter<TruthReviewQueue>
  applyReviewQueue: (payload?: TruthReviewQueue | null) => void
}

const DEFAULT_QUEUE: TruthReviewQueue = Object.freeze({
  groups: [],
  coverage: null,
  suggestions: [],
}) as TruthReviewQueue

const noop = (): void => {}

const TruthContext = createContext<TruthContextValue>({
  suggestions: [],
  reviewQueue: DEFAULT_QUEUE,
  setSuggestions: noop,
  setReviewQueue: noop,
  applyReviewQueue: noop,
})

export interface TruthProviderProps {
  children?: ReactNode
  initialState?: {
    suggestions?: TruthSuggestion[]
    reviewQueue?: TruthReviewQueue
  }
}

export function TruthProvider({
  children,
  initialState = {},
}: TruthProviderProps) {
  const [suggestions, setSuggestions] = useState<TruthSuggestion[]>(
    initialState.suggestions || []
  )
  const [reviewQueue, setReviewQueue] = useState<TruthReviewQueue>(
    initialState.reviewQueue || { ...DEFAULT_QUEUE }
  )

  // Mirrors the legacy App.jsx `applyTruthReviewQueue`: stores the full
  // queue and, when the payload includes a `suggestions` array, mirrors
  // it into the flat list so existing consumers stay consistent.
  const applyReviewQueue = useCallback((payload?: TruthReviewQueue | null) => {
    const next: TruthReviewQueue = payload || { ...DEFAULT_QUEUE }
    setReviewQueue(next)
    if (Array.isArray(next.suggestions)) {
      setSuggestions(next.suggestions)
    }
  }, [])

  const value = useMemo<TruthContextValue>(
    () => ({
      suggestions,
      reviewQueue,
      setSuggestions,
      setReviewQueue,
      applyReviewQueue,
    }),
    [suggestions, reviewQueue, applyReviewQueue]
  )

  return <TruthContext.Provider value={value}>{children}</TruthContext.Provider>
}

export function useTruth(): TruthContextValue {
  return useContext(TruthContext)
}

export { DEFAULT_QUEUE, TruthContext }
